import type { FunctionComponent } from 'react';
import { useRouter } from 'next/router';

// Hooks & Helpers
import { useMonarchSlotContext } from '@redventures/cohesion-utils-react';
import { useVariantIds } from '~/lib/hooks/useVariantIds';
import { WALMART_URLS } from '../../../config/urls';

// Components
import CTAButton, { CTAColorVariant } from '~/components/shared/CTAButton';
import LinkWrapper from '~/components/shared/LinkWrapper/linkWrapper';
import Image from 'next-export-optimize-images/image';

// Classes
import * as classes from './slimmedNavbarClasses';
const {
  logoWrapperClasses,
  headerLinksWrapperClasses,
  signInWrapperClasses,
  signInButtonWrapperClasses,
  desktopTextClasses,
  signInButtonImageWrapperClasses,
  ctaClasses,
  getHeaderClasses,
} = classes;

export interface SlimmedNavbarProps {
  CTAButtonText?: string;
  CTAButtonVariant?: CTAColorVariant;
  CTAButtonLink?: string;
  signInButton?: boolean;
}

const DEFAULT_PROPS = {
  CTAButtonText: 'Claim offer now',
  CTAButtonVariant: 'secondary' as CTAColorVariant,
  CTAButtonLink:
    'https://www.walmart.com/?wplusSignupOffer=true&programId=discmemb23',
  signInButton: false,
};

const STUDENT_PROPS = {
  CTAButtonText: 'Claim your free trial',
  CTAButtonVariant: 'secondary' as CTAColorVariant,
  CTAButtonLink: WALMART_URLS.PLUS_STUDENT_SIGNUP,
  signInButton: false,
};

const SlimmedNavbar: FunctionComponent<SlimmedNavbarProps> = (props) => {
  const { data } = useMonarchSlotContext<typeof DEFAULT_PROPS>();
  const { isDarkMode } = useVariantIds();
  const router = useRouter();
  const isStudentPage = router.pathname.startsWith('/student');
  const baseProps = isStudentPage ? STUDENT_PROPS : DEFAULT_PROPS;
  const mergedData = {
    ...baseProps,
    ...data,
    ...props,
  };
  const { CTAButtonText, CTAButtonVariant, CTAButtonLink, signInButton } =
    mergedData;

  const imgPath = `${process.env.domainPath}/images/sections/hero/`;
  const headerClasses = getHeaderClasses(isDarkMode);

  return (
    <div className={headerClasses}>
      <LinkWrapper
        href='https://walmart.com'
        className={logoWrapperClasses}
        segmentTracking={{
          elementName: 'WALMART_LOGO',
          elementPosition: 'NAV',
        }}
      >
        <Image
          src={`${imgPath}rebrand-walmart-logo-white.svg`}
          alt='Walmart+ Logo'
          width={94}
          height={32}
          className='w-auto'
          priority={true}
        />
      </LinkWrapper>
      <div className={headerLinksWrapperClasses}>
        <div>
          <CTAButton
            className={ctaClasses}
            variant={CTAButtonVariant}
            size='thin'
            elementPosition='NAVBAR'
            elementType='SIGN_UP_CTA_PRIMARY'
            href={CTAButtonLink}
          >
            {CTAButtonText}
          </CTAButton>
        </div>
        {signInButton && (
          <div className={signInWrapperClasses}>
            <LinkWrapper
              href={CTAButtonLink}
              className={signInButtonImageWrapperClasses}
              segmentTracking={{
                elementName: 'SIGN_IN_BUTTON',
                elementPosition: 'NAV',
              }}
            >
              <Image
                src={`${imgPath}person-white.svg`}
                alt='Navbar person image'
                width={16}
                height={18}
                className='w-auto'
                priority={true}
              />
            </LinkWrapper>
            <div className={signInButtonWrapperClasses}>
              <LinkWrapper
                href={CTAButtonLink}
                className={desktopTextClasses}
                segmentTracking={{
                  elementName: 'SIGN_IN_BUTTON',
                  elementPosition: 'NAV',
                }}
              >
                Sign In
              </LinkWrapper>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SlimmedNavbar;
